import { Link } from "react-router-dom";
import usePageMeta from "../hooks/usePageMeta";
import { ARTICLES, SECTIONS } from "../content/articles";
import { ARTICLE_MEDIA, figureInfo } from "../content/articleImages";
import { SEO_SITE_URL } from "../lib/siteConfig";

const anchor = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const CardThumb = ({ slug }) => {
  const media = ARTICLE_MEDIA[slug];
  if (!media) return null;
  const f = figureInfo(media.primary);
  const small = f.product ? f.src.replace("-800.jpg", "-480.webp").replace("-800.png", "-480.webp") : f.srcSmall;
  return f.product ? (
    <span className="photo-panel h-28 mb-3 flex items-center justify-center overflow-hidden">
      <img src={small} alt={f.alt} width="72" height="96" loading="lazy" className="h-24 w-auto object-contain" />
    </span>
  ) : (
    <img src={small} alt={f.alt} width="320" height="140" loading="lazy" className="h-28 w-full object-cover mb-3 border border-[#EBD9D0]" />
  );
};

export default function JournalHub() {
  const jsonLd = [{
    "@context": "https://schema.org", "@type": "ItemList",
    name: "Tiff. Wellness Journal articles",
    numberOfItems: ARTICLES.length,
    itemListElement: ARTICLES.map((a, i) => ({ "@type": "ListItem", position: i + 1, name: a.title, url: `${SEO_SITE_URL}/journal/${a.slug}` })),
  }];
  usePageMeta("Dallas Juice Guides & Journal | Tiff. Wellness Co.",
    "Plain-language guides to buying cold-pressed juice in Dallas: flavors, labels, functional additions, gym pickup logistics and subscription decisions.",
    { schemaType: "CollectionPage", jsonLd });

  return (
    <div className="max-w-4xl mx-auto px-4 py-12" data-testid="journal-hub">
      <p className="eyebrow mb-3">Journal</p>
      <h1 className="text-4xl sm:text-5xl mb-4">The Tiff. Wellness Journal</h1>
      <p className="text-sm text-[#171715]/75 max-w-2xl mb-8">Practical, no-hype guides to choosing juice in Dallas — ingredients, labels, pickup logistics and subscriptions. Written by the Tiff. Wellness Co. team, with sources cited for any science and honest caveats where we haven't verified something.</p>

      <nav aria-label="Journal categories" className="flex flex-wrap gap-2 mb-10" data-testid="journal-category-nav">
        {SECTIONS.map((s) => (
          <a key={s} href={`#${anchor(s)}`} className="btn-ghost text-xs px-3 py-1.5" data-testid={`journal-cat-link-${anchor(s)}`}>{s}</a>
        ))}
      </nav>

      {SECTIONS.map((s) => (
        <section key={s} id={anchor(s)} className="mb-12" data-testid={`journal-section-${anchor(s)}`}>
          <h2 className="text-2xl font-medium mb-5">{s}</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {ARTICLES.filter((a) => a.section === s).map((a) => (
              <Link key={a.slug} to={`/journal/${a.slug}`} className="card-paper p-5 block hover:shadow-md transition-shadow" data-testid={`journal-card-${a.slug}`}>
                <CardThumb slug={a.slug} />
                <p className="font-medium leading-snug">{a.title}</p>
                <p className="text-xs text-[#171715]/65 mt-2 line-clamp-3">{a.metaDescription}</p>
                <p className="text-xs text-[#A35847] underline mt-3">Read the guide</p>
              </Link>
            ))}
          </div>
        </section>
      ))}

      <p className="text-xs text-[#171715]/55 border-t border-[#EBD9D0] pt-4">Looking for the juices themselves? Browse the <Link to="/shop" className="underline">full menu</Link>, <Link to="/build-your-stack" className="underline">build a mixed pack</Link> or check <Link to="/pickup" className="underline">pickup details</Link>.</p>
    </div>
  );
}
